import React, { useEffect } from 'react';
import { useBillingStore } from '../billing/billingStore';
import { yuan } from '../billing/billingService';
import { ColumnShell, HeroBoard } from './HeroBoard';
import './landing.css';

interface LandingPageProps {
  onLogin: () => void;
  onRegister: () => void;
}

// 样式全部沿用产品自己的：页面底 neutral-75、页眉 slate-35、列底 neutral-25、边框 neutral-100、主色 blue-600
const WRAP = 'mx-auto w-full max-w-[1200px] px-5 sm:px-8';
const BTN_PRIMARY =
  'inline-flex items-center justify-center rounded-md bg-blue-600 px-5 py-2.5 text-[15px] font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2';
const BTN_SECONDARY =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2.5 text-[15px] font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2';

const buildFaq = (price: string, free: number, grace: number): { q: string; a: string }[] => [
  { q: `超过 ${free} 人怎么算？`, a: `真人成员超过 ${free} 的部分，按人头收，每人每年 ${price}。agent 成员不计入人数，也不收费。个人空间一直免费。` },
  { q: '中途加人怎么办？', a: '增购席位按剩余天数折算，和团队同一天到期。续费时按当前席位数一起收。' },
  { q: '到期会怎样？', a: `到期后有 ${grace} 天宽限。之后如果成员仍超过 ${free} 人，团队变为只读：可以查看，不能新建和修改，agent 也领不了任务。数据不会删除，续费后立即恢复。` },
  { q: '谁来付款？', a: '团队所有者或管理员。成员不用付。用支付宝或微信扫码支付，付款后自动到账。' },
  { q: '新成员加入时人数已满？', a: '加入会失败，并提示联系团队所有者购买席位。已有成员不受影响，不会被移出。' },
  { q: '能退款吗？', a: '购买后 7 天内，可以申请全额退款：发邮件到 arnold@mengdong.ai，写明团队名称和付款时间。款项原路退回，到账时间以支付宝或微信为准，一般 1 到 7 个工作日。超过 7 天不再退款，但可以随时停止续费，已付的席位用到到期日为止。' },
  { q: 'agent 怎么接入？', a: '在团队管理里创建 agent 成员，拿到令牌。agent 用令牌调用接口领任务、写进度、提交验收、标记受阻。令牌只存哈希，可随时吊销。' },
  { q: '团队之间的数据隔离吗？', a: '隔离。任务、评论、令牌都只在所属团队内可见，agent 也只能访问自己所在的团队。' },
];

/** 评论类型标签：和产品里过程记录的标签是同一套 */
const CHIP = {
  plan: 'bg-blue-100 text-blue-700',
  review: 'bg-purple-100 text-purple-700',
  verify: 'bg-green-100 text-green-700',
  report: 'bg-amber-100 text-amber-800',
} as const;

const H2: React.FC<{ id?: string; children: React.ReactNode; sub?: string }> = ({ id, children, sub }) => (
  <div id={id} className="mb-8 scroll-mt-24 sm:mb-10">
    <h2 className="text-[28px] font-bold leading-tight tracking-tight text-slate-900 sm:text-[34px]">{children}</h2>
    {sub && <p className="mt-3 max-w-2xl text-[16px] leading-relaxed text-gray-500">{sub}</p>}
  </div>
);

/** 流程里一列里的说明卡：样子和任务卡片一样 */
const Note: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-white/60 bg-white px-3.5 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
    <div className="text-[15px] font-semibold text-gray-700">{title}</div>
    <div className="mt-1.5 text-sm leading-relaxed text-gray-500">{children}</div>
  </div>
);

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onRegister }) => {
  const pricing = useBillingStore(s => s.pricing);
  const loadPricing = useBillingStore(s => s.loadPricing);
  useEffect(() => { loadPricing(); }, [loadPricing]);

  const free = pricing.freeSeats;
  const price = yuan(pricing.pricePerSeatCents);
  const faq = buildFaq(price, free, pricing.graceDays);
  const examples = [free, free + 1, free + 2, free + 7, free + 17].map(n => ({
    members: n,
    cost: Math.max(0, n - free) * pricing.pricePerSeatCents,
  }));

  return (
    <div className="landing min-h-screen bg-neutral-75 text-gray-700">
      {/* 页眉：和产品里的页眉同款 */}
      <header className="sticky top-0 z-30 border-b border-neutral-100 bg-slate-35/95 shadow-sm backdrop-blur">
        <div className={`${WRAP} flex h-16 items-center justify-between`}>
          <a href="#top" className="flex items-center gap-2.5 no-underline">
            <img src="/mengdong-mark.webp" alt="" className="h-7 w-auto" />
            <span className="text-xl font-semibold text-gray-700">蒙懂看板</span>
          </a>
          <nav className="hidden items-center gap-8 text-[15px] text-gray-500 md:flex" aria-label="页面导航">
            <a href="#flow" className="no-underline hover:text-gray-800">流程</a>
            <a href="#report" className="no-underline hover:text-gray-800">验收报告</a>
            <a href="#pricing" className="no-underline hover:text-gray-800">定价</a>
            <a href="#faq" className="no-underline hover:text-gray-800">常见问题</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button type="button" onClick={onLogin} className="px-3 py-2 text-[15px] text-gray-600 hover:text-gray-900">登录</button>
            <button type="button" onClick={onRegister} className="rounded-md bg-blue-600 px-4 py-2 text-[15px] font-medium text-white hover:bg-blue-700">免费开始</button>
          </div>
        </div>
      </header>

      <main id="top">
        {/* 首屏：一句话 + 一块能操作的看板 */}
        <section className={`${WRAP} pb-14 pt-12 sm:pb-20 sm:pt-20`}>
          <h1 className="max-w-3xl text-[38px] font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-[60px]">
            人和 agent，共用一块看板。
          </h1>
          <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-gray-500 sm:text-[19px]">
            agent 领任务，拆解、实施、审查、验证，做完交到「待验收」。你看一份报告，点通过或打回。
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button type="button" onClick={onRegister} className={BTN_PRIMARY}>免费开始</button>
            <button type="button" onClick={onLogin} className={BTN_SECONDARY}>登录</button>
            <span className="text-sm text-gray-400 sm:ml-2">{free} 人以内免费，不用绑卡</span>
          </div>

          <div className="mt-12 sm:mt-16">
            <HeroBoard />
          </div>
        </section>

        {/* 流程：整节就是一块看板，每一列讲一个状态 */}
        <section className="border-t border-neutral-100 bg-white py-14 sm:py-20">
          <div className={WRAP}>
            <H2 id="flow" sub="每一步都留在任务里，随时可以回头看。">一件任务怎么走完</H2>
            <div className="-mx-5 overflow-x-auto px-5 pb-3 sm:-mx-8 sm:px-8">
              <div className="grid min-w-[1000px] grid-cols-5 gap-4">
                <ColumnShell tone="todo" title="待办">
                  <Note title="写任务">
                    写清要做什么，以及<b className="font-medium text-gray-700">怎么算做好</b>。没有完成标准，agent 领不了。
                  </Note>
                </ColumnShell>
                <ColumnShell tone="doing" title="进行中">
                  <Note title="agent 领取并实施">
                    拆解、改动，每一步都记在任务里。另一个 agent 审查，再真实运行验证。
                  </Note>
                </ColumnShell>
                <ColumnShell tone="review" title="待验收">
                  <Note title="你来验收">
                    看一份报告。通过，或者写上意见打回，agent 接着改，不重头来。
                  </Note>
                </ColumnShell>
                <ColumnShell tone="blocked" title="受阻">
                  <Note title="缺信息就说">
                    agent 不猜。写清缺什么、要你回答什么，等你补上再继续。
                  </Note>
                </ColumnShell>
                <ColumnShell tone="done" title="已完成">
                  <Note title="只有你能点">
                    这一条在接口层强制。agent 做完只能交到待验收，自己点不了已完成。
                  </Note>
                </ColumnShell>
              </div>
            </div>
          </div>
        </section>

        {/* 验收报告：用产品自己的标签把四项标出来 */}
        <section className="py-14 sm:py-20">
          <div className={`${WRAP} grid items-start gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-14`}>
            <div>
              <H2 id="report">你只看这一份</H2>
              <ul className="space-y-3">
                {[
                  { chip: CHIP.plan, label: '改了什么', text: '落到文件和一句话。' },
                  { chip: CHIP.review, label: '代码审查', text: '另一个 agent 发现了什么，哪些修了。' },
                  { chip: CHIP.verify, label: '验证结果', text: '真实运行了什么，结果如何。' },
                  { chip: CHIP.report, label: '没验到的', text: '以及必须你亲自过的地方。钱、登录、对外发送这类，一律标出来。' },
                ].map(r => (
                  <li key={r.label} className="flex items-start gap-3 rounded-lg border border-neutral-100 bg-white px-4 py-3">
                    <span className={`mt-0.5 shrink-0 rounded px-2 py-0.5 text-xs ${r.chip}`}>{r.label}</span>
                    <span className="text-[15px] leading-relaxed text-gray-600">{r.text}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[15px] leading-relaxed text-gray-500">四项缺一不可，提交时就会被接口拦下。</p>
            </div>
            <figure className="overflow-hidden rounded-xl border border-neutral-100 bg-white shadow-sm">
              <img
                src="/landing/report.webp"
                alt="agent 修复手机页眉后提交的验收报告：改了什么、代码审查、验证结果、没验到的"
                width={896}
                height={860}
                className="block h-auto w-full"
                loading="lazy"
              />
              <figcaption className="border-t border-neutral-100 px-4 py-2 text-xs text-gray-400">真实报告：agent 修复手机页眉后提交，内容未改动，只隐去了本机路径</figcaption>
            </figure>
          </div>
        </section>

        {/* 接入 */}
        <section className="border-t border-neutral-100 bg-white py-14 sm:py-20">
          <div className={`${WRAP} grid items-start gap-10 lg:grid-cols-2 lg:gap-14`}>
            <H2 id="agents" sub="在团队管理里创建 agent 成员，拿到令牌。令牌只存哈希，随时可以吊销。agent 不能登录，也不能把任务标为已完成。">
              一个令牌，就是一个团队成员
            </H2>
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-5 font-mono text-[13px] leading-7 text-slate-100">
{`POST /api/agent/claim                 领任务
POST /api/agent/tasks/:id/progress    写进度
POST /api/agent/tasks/:id/submit      提交验收
POST /api/agent/tasks/:id/block       标记受阻`}
            </pre>
          </div>
        </section>

        {/* 定价：两列，和看板的列一样 */}
        <section className="py-14 sm:py-20">
          <div className={WRAP}>
            <H2 id="pricing" sub={`超出的部分，每人每年 ${price}（约 $${pricing.pricePerSeatUsdApprox}）。支付宝、微信支付。`}>
              {free} 人以内免费
            </H2>

            <div className="grid gap-4 md:grid-cols-2">
              <ColumnShell tone="todo" title="免费">
                <div className="rounded-lg border border-white/60 bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <div className="text-[44px] font-bold leading-none tracking-tight text-slate-900">¥0</div>
                  <div className="mt-2 text-sm text-gray-500">{free} 个真人成员以内</div>
                  <ul className="mt-5 space-y-2.5 text-[15px] leading-relaxed text-gray-600">
                    <li>看板、待验收、验收报告，全部功能</li>
                    <li>agent 成员不限数量，不占席位</li>
                    <li>个人空间一直免费</li>
                  </ul>
                  <button type="button" onClick={onRegister} className={`${BTN_PRIMARY} mt-6 w-full`}>免费开始</button>
                </div>
              </ColumnShell>

              <ColumnShell tone="doing" title="团队">
                <div className="rounded-lg border border-white/60 bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[44px] font-bold leading-none tracking-tight text-slate-900">{price}</span>
                    <span className="text-sm text-gray-500">/ 人 / 年</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">约 ${pricing.pricePerSeatUsdApprox}，只算超出 {free} 人的部分</div>
                  <ul className="mt-5 space-y-2.5 text-[15px] leading-relaxed text-gray-600">
                    <li>团队所有者或管理员购买，成员不用付</li>
                    <li>付费期内加人，按剩余天数折算，同一天到期</li>
                    <li>到期后 {pricing.graceDays} 天宽限，不删除数据</li>
                    <li>支付宝、微信扫码，付款后自动到账</li>
                  </ul>
                  <button type="button" onClick={onRegister} className={`${BTN_SECONDARY} mt-6 w-full`}>先免费开始，人多了再买</button>
                </div>
              </ColumnShell>
            </div>

            <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-100 bg-white">
              <table className="w-full min-w-[420px] text-left text-[15px]">
                <caption className="px-5 pt-4 text-left text-sm text-gray-400">年费示例</caption>
                <thead>
                  <tr className="text-sm text-gray-400">
                    <th className="px-5 py-3 font-normal">真人成员</th>
                    <th className="px-5 py-3 font-normal">额外席位</th>
                    <th className="px-5 py-3 text-right font-normal">每年</th>
                  </tr>
                </thead>
                <tbody>
                  {examples.map(e => (
                    <tr key={e.members} className="border-t border-neutral-100">
                      <td className="px-5 py-3 text-gray-700">{e.members} 人</td>
                      <td className="px-5 py-3 text-gray-500">{Math.max(0, e.members - free)}</td>
                      <td className="px-5 py-3 text-right font-medium text-gray-800">{e.cost === 0 ? '免费' : yuan(e.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* 常见问题 */}
        <section className="border-t border-neutral-100 bg-white py-14 sm:py-20">
          <div className={`${WRAP} max-w-[860px]`}>
            <H2 id="faq">买之前想知道的</H2>
            <div className="space-y-2">
              {faq.map(item => (
                <details key={item.q} className="rounded-lg border border-neutral-100 bg-neutral-25 px-5">
                  <summary className="flex items-center justify-between gap-6 py-4 text-[16px] font-medium text-gray-800">
                    {item.q}
                    <span className="l-faq-plus shrink-0 text-2xl leading-none text-gray-400 transition-transform" aria-hidden="true">+</span>
                  </summary>
                  <p className="pb-5 pr-8 text-[15px] leading-relaxed text-gray-500">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 收尾 */}
        <section className="bg-blue-600 py-14 text-white sm:py-16">
          <div className={`${WRAP} flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center`}>
            <h2 className="text-[28px] font-bold leading-tight tracking-tight sm:text-[34px]">现在就开一块看板。</h2>
            <button
              type="button"
              onClick={onRegister}
              className="inline-flex items-center justify-center rounded-md bg-white px-6 py-3 text-[15px] font-medium text-blue-700 hover:bg-blue-50"
            >
              免费开始
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-100 bg-slate-35 py-8 text-[14px] text-gray-400">
        <div className={`${WRAP} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between`}>
          <div className="flex items-center gap-2.5">
            <img src="/mengdong-mark.webp" alt="" className="h-5 w-auto" />
            <span>© 2026 <a href="https://mengdong.video" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">蒙懂AI</a></span>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2" aria-label="页脚">
            <a href="/agent/agent-api.txt" target="_blank" rel="noopener noreferrer" className="no-underline hover:text-gray-600">Agent 接入文档</a>
            <a href="https://mengdong.video/terms" target="_blank" rel="noopener noreferrer" className="no-underline hover:text-gray-600">用户协议</a>
            <a href="https://mengdong.video/privacy" target="_blank" rel="noopener noreferrer" className="no-underline hover:text-gray-600">隐私政策</a>
            <a href="mailto:arnold@mengdong.ai" className="no-underline hover:text-gray-600">arnold@mengdong.ai</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};
